import classNames from "classnames/dedupe";
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { CSSTransition } from "react-transition-group";
import { v5 as uuidv5 } from "uuid";
import { DEFAULT_UID_NAMESPACE } from "../../../helpers";
import useEventListener from "../../../hooks/useEventListener";
import { DropdownContext } from "./Dropdown.context";
import {
  DropdownDrawingStrategy,
  DropdownPosition,
} from "./Dropdown.interface";
import { IDropdownListProps } from "./DropdownList.interface";
import { setDropdownPos } from "./setDropdownPos";
import { useBodyOverflow } from "../../../hooks";

const initDropdownId = () => uuidv5("dropdown", DEFAULT_UID_NAMESPACE);

const DropdownList: FC<IDropdownListProps> = ({
  classes,
  animation,
  reposition = false,
  mask,
  children,
  hasFixedDropdown = false,
  fixedPositions,
  hasTableDisplay = false,
  isInFilter = false,
  onContainerClick = () => {},
}) => {
  const dropdownCtx = useContext(DropdownContext);

  const [id] = useState<string>(initDropdownId);

  const {
    opened,
    position,
    alignment,
    currentAlignment,
    currentPosition,
    setPos,
    setCurrentAlignment,
    setCurrentPosition,
    buttonRef,
    listRef,
    drawingStrategy = DropdownDrawingStrategy.JS,
  } = dropdownCtx;
  const calculateConfig = useMemo(() => ({ passive: true, capture: true }), []);

  /** Disable animation for dropping up */
  const hasAnimation = currentPosition !== DropdownPosition.TOP && animation;

  const handleCalc = useCallback(() => {
    const btn = buttonRef?.current?.children[0];
    const list = listRef?.current;
    if (!btn || !list) return;

    const mutateDOM = drawingStrategy === DropdownDrawingStrategy.JS;

    const {
      currentAlignment: newCurrentAlignment,
      currentPosition: newCurrentPosition,
      pos,
    } = setDropdownPos({
      mutateDOM,
      btn,
      list,
      alignment,
      position,
      reposition,
      hasFixedDropdown,
      fixedPositions,
    });

    newCurrentAlignment && setCurrentAlignment(newCurrentAlignment);
    newCurrentPosition && setCurrentPosition(newCurrentPosition);
    pos && setPos(pos);
  }, [
    buttonRef,
    listRef,
    drawingStrategy,
    alignment,
    position,
    reposition,
    setCurrentAlignment,
    setCurrentPosition,
    setPos,
    hasFixedDropdown,
    fixedPositions,
  ]);

  useEventListener("resize", handleCalc, calculateConfig);
  useEventListener("resize", handleCalc);
  /** !!! careful, can cause performance impact !! */
  useEventListener("scroll", handleCalc, calculateConfig);

  useEffect(() => {
    // Always execute handleCalc regardless of opened state
    if (drawingStrategy === DropdownDrawingStrategy.CSS || hasAnimation) {
      handleCalc();
    }
    if (opened) {
      if (listRef?.current) {
        listRef.current.style.display = "block";
      }
      handleCalc();
    } else if (listRef?.current) {
      listRef.current.style.display = "none";
    }
  }, [listRef, handleCalc, opened, drawingStrategy, hasAnimation]);

  useBodyOverflow(
    isInFilter && listRef?.current?.style.display === "block" && opened,
  );

  const content = (
    <div
      onClick={onContainerClick}
      data-testid="DropdownList"
      ref={listRef}
      className={classNames(
        "dropdown__list",
        {
          [animation]: hasAnimation,
          "dropdown__list--opened": opened,
          "dropdown__list--css":
            drawingStrategy === DropdownDrawingStrategy.CSS,
          "position-fixed": hasFixedDropdown,
          "d-table": hasTableDisplay && opened,
        },
        [`dropdown__list--alignment-${currentAlignment}`],
        [`dropdown__list--pos-${currentPosition}`],
        classes?.root,
      )}
    >
      {children(dropdownCtx)}
    </div>
  );

  return (
    <>
      {hasAnimation ? (
        <CSSTransition
          key={id}
          appear
          classNames={animation}
          in={opened}
          nodeRef={listRef}
          timeout={300}
          onEnter={handleCalc}
          onEntering={handleCalc}
          onEntered={handleCalc}
          onExiting={handleCalc}
          onExit={handleCalc}
          onExited={handleCalc}
        >
          {content}
        </CSSTransition>
      ) : (
        content
      )}
      {mask ? mask(dropdownCtx) : null}
    </>
  );
};

export default DropdownList;
